/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import IconUp from "@mui/icons-material/ExpandLess";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import makeStyles from "@mui/styles/makeStyles";
import debounce from "lodash/debounce";
import { bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExpansionIcon } from "../../../expansion-icon";
import { Menu } from "../../../menu";
import { MapProjection } from "../../utils";

export interface BasemapOption {
  /** Identifies the basemap to the app; not shown to the user. */
  slug: string;
  name: string;
  /** Thumbnail shown beside the name. */
  background?: string;
}

interface IMapControlsMenuProps {
  className?: string;
  basemaps: BasemapOption[];
  /** Slug of the basemap in use. */
  basemap: string;
  onChangeBasemap: (slug: string) => void;
  /** Whether the basemap's own labels and roads are drawn. */
  mapLabels: boolean;
  onChangeMapLabels: (value: boolean) => void;
  mapRoads: boolean;
  onChangeMapRoads: (value: boolean) => void;
  /** The projection the map renders in. */
  mapProjection: MapProjection;
  onChangeMapProjection: (projection: MapProjection) => void;
  /** True while an active layer can't be drawn on the globe */
  globeDisabled?: boolean;
}

const useStyles = makeStyles(() => ({
  loadingIndicator: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.25)",
  },
  mapBackground: {
    position: "relative",
    backgroundPosition: "center",
    backgroundSize: "cover",
  },
  mapControlsPopover: {
    "& .MuiMenu-paper": "none",
    "& .MuiMenu-list": {
      pointerEvents: "all",
    },
  },
}));

const MapControlListItem = (props: { avatar?: any; label: any }) => {
  const { avatar, label } = props;

  return (
    <>
      {avatar && <ListItemAvatar>{avatar}</ListItemAvatar>}
      <ListItemText primary={label} />
    </>
  );
};

/**
 * The map's own controls: which basemap to show, and where the app supports
 * them, whether to draw labels and roads over it.
 */
export const MapControlsMenu = (props: IMapControlsMenuProps) => {
  const {
    className,
    basemaps,
    basemap,
    onChangeBasemap,
    mapLabels,
    onChangeMapLabels,
    mapRoads,
    onChangeMapRoads,
    mapProjection,
    onChangeMapProjection,
    globeDisabled = false,
  } = props;

  const classes = useStyles();
  const { t } = useTranslation();

  const [loadingBasemap, setLoadingBasemap] = useState(null);

  useEffect(() => {
    setTimeout(() => setLoadingBasemap(null), 1000);
  }, [loadingBasemap]);

  const popupState = usePopupState({
    variant: "popover",
    popupId: "mapControlsMenu",
  });

  const onSelectBaseMap = (slug) => {
    if (slug !== basemap) {
      onChangeBasemap(slug);
      setLoadingBasemap(slug);
    }
  };

  const isGlobe = mapProjection === MapProjection.GLOBE;

  const onSetGlobe = () =>
    onChangeMapProjection(
      isGlobe ? MapProjection.MERCATOR : MapProjection.GLOBE
    );

  const getOptionCommonProps = (onClick) => ({
    onClick: debounce(onClick, 200),
    disableCloseOnOptionClick: true,
  });

  return (
    <Box
      className={["marapp-qa-map-controls", className]
        .filter(Boolean)
        .join(" ")}
    >
      <Paper elevation={8} sx={{ boxShadow: 2, textAlign: "center" }}>
        <Button
          className="marapp-qa-map-controls-toggle"
          {...bindTrigger(popupState)}
          size="small"
          color="secondary"
          variant="text"
          sx={{ height: 28, paddingLeft: 2 }}
          endIcon={<ExpansionIcon icon={IconUp} isActive={popupState.isOpen} />}
        >
          {t("Map Controls")}
        </Button>

        <Menu
          className={classes.mapControlsPopover}
          anchorOrigin={{
            vertical: -1,
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          popupState={popupState}
          onBlur={null}
          options={[
            {
              className: "marapp-qa-map-globe-toggle",
              "aria-disabled": globeDisabled,
              label: (
                // Not `disabled` on the item: MUI drops pointer events on
                // disabled items, which would swallow the tooltip
                <Tooltip
                  title={
                    globeDisabled
                      ? t("Not available with the selected layers")
                      : ""
                  }
                  placement="left"
                >
                  <Box display="flex" alignItems="center" width="100%">
                    <MapControlListItem
                      label={t("3D Globe")}
                      avatar={
                        <Box ml={-1}>
                          <Switch
                            className="marapp-qa-map-globe-toggle"
                            checked={isGlobe}
                            disabled={globeDisabled}
                          />
                        </Box>
                      }
                    />
                  </Box>
                </Tooltip>
              ),
              ...getOptionCommonProps(
                globeDisabled ? () => undefined : onSetGlobe
              ),
            },
            ...basemaps.map(({ background, name, slug }) => ({
              className: `marapp-qa-base-map marapp-qa-base-map-${name}`,
              selected: slug === basemap,
              label: (
                <MapControlListItem
                  label={t(name)}
                  avatar={
                    <Avatar
                      className={classes.mapBackground}
                      sx={{
                        backgroundImage: `url(${background})`,
                      }}
                      variant="rounded"
                    >
                      {loadingBasemap === slug ? (
                        <div className={classes.loadingIndicator}>
                          <CircularProgress size={16} />
                        </div>
                      ) : (
                        <>&nbsp;</>
                      )}
                    </Avatar>
                  }
                />
              ),
              ...getOptionCommonProps(() => onSelectBaseMap(slug)),
            })),
            {
              className: "marapp-qa-map-labels-toggle",
              label: (
                <MapControlListItem
                  label={t("Labels")}
                  avatar={
                    <Box ml={-1}>
                      <Switch
                        className="marapp-qa-map-labels-toggle"
                        checked={!!mapLabels}
                      />
                    </Box>
                  }
                />
              ),
              ...getOptionCommonProps(() => onChangeMapLabels(!mapLabels)),
            },
            {
              className: "marapp-qa-map-roads-toggle",
              label: (
                <MapControlListItem
                  label={t("Roads")}
                  avatar={
                    <Box ml={-1}>
                      <Switch
                        className="marapp-qa-map-roads-toggle"
                        checked={!!mapRoads}
                      />
                    </Box>
                  }
                />
              ),
              ...getOptionCommonProps(() => onChangeMapRoads(!mapRoads)),
            },
          ]}
        />
      </Paper>
    </Box>
  );
};

export default MapControlsMenu;
